import {
  type ChartIndexRange,
  mergeViewRangeIntoAbsolute,
  sliceByIndexRange,
} from "@/components/charts/svg-chart-interactions";
import {
  AttomExtractHistoryChart,
  AttomJurisdictionsByStateChart,
  AttomRecordsByStateChart,
  ChartRangeResetButton,
} from "@/components/non_registrant/attom-tax-assessor-charts";
import { DashboardHeader } from "@/components/non_registrant/dashboard-header";
import { OPERATOR_SECTION_BLURBS } from "@/components/non_registrant/portal-section-helpers";
import { PortalTileTitle } from "@/components/portal-tile-title";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import { Select, type SelectOption } from "@/components/ui/select";
import {
  type AttomTaxAssessorStatusResponse,
  getAttomIntegrationStatus,
  getAttomTaxAssessorStatus,
} from "@/hera-api/attom-tax-assessor-api";
import { getPortalPageTitleForSection } from "@/lib/portal-nav";
import { getQueryErrorMessage } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

function formatCount(value: number | undefined): string {
  if (value == null) {
    return "—";
  }
  return value.toLocaleString();
}

function AttomStatCard({
  title,
  value,
  description,
}: {
  title: string;
  value: string | number;
  description: string;
}) {
  return (
    <Card className="min-w-[200px] flex-shrink-0 gap-0">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <PortalTileTitle title={title} className="text-sm font-medium" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
}

function summarizeLatestExtract(payload: AttomTaxAssessorStatusResponse): {
  latestRecords: number | undefined;
  latestJurisdictions: number | undefined;
} {
  const latestDate = payload.latest_extract_date;
  if (!latestDate) {
    return { latestRecords: undefined, latestJurisdictions: undefined };
  }

  const latestExtractRow = payload.by_extract_date.find(
    (row) => row.extract_date === latestDate,
  );
  if (latestExtractRow) {
    return {
      latestRecords: latestExtractRow.record_count,
      latestJurisdictions: latestExtractRow.jurisdiction_count,
    };
  }

  const latestRecords = payload.latest_by_state.reduce(
    (total, row) => total + row.record_count,
    0,
  );
  const latestJurisdictions = payload.latest_by_state.reduce(
    (total, row) => total + row.jurisdiction_count,
    0,
  );

  return { latestRecords, latestJurisdictions };
}

export function DashboardAttomTaxAssessorStatus({
  isAuthSetupComplete,
  isActive = true,
  embedded = false,
}: {
  isAuthSetupComplete: boolean;
  /** When false, Athena queries are not fetched (e.g. setup tab not selected). */
  isActive?: boolean;
  /** When true, omit the page title (used inside Setup). */
  embedded?: boolean;
}) {
  const [stateFilter, setStateFilter] = useState<string>("");
  const [chartRange, setChartRange] = useState<ChartIndexRange | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset chart zoom when state filter changes
  useEffect(() => {
    setChartRange(null);
  }, [stateFilter]);
  const section = "attom-tax-assessor-status";
  const blurb = OPERATOR_SECTION_BLURBS[section] ?? "";
  const queriesEnabled = isAuthSetupComplete && isActive;

  const { data: integrationStatus } = useQuery({
    queryKey: ["attom-integration-status"],
    queryFn: getAttomIntegrationStatus,
    enabled: queriesEnabled,
  });

  const { data, error, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["attom-tax-assessor-status", stateFilter],
    queryFn: () =>
      getAttomTaxAssessorStatus({
        state: stateFilter || undefined,
      }),
    enabled: queriesEnabled,
    staleTime: 0,
  });

  const supportedStates = useMemo(
    () => data?.supported_states ?? [],
    [data?.supported_states],
  );

  const stateOptions = useMemo<SelectOption[]>(
    () => [
      { value: "", label: "All states" },
      ...supportedStates.map((state) => ({ value: state, label: state })),
    ],
    [supportedStates],
  );

  const extractDates = useMemo(
    () =>
      [...(data?.by_extract_date ?? [])]
        .map((row) => row.extract_date)
        .sort((a, b) => a.localeCompare(b)),
    [data?.by_extract_date],
  );

  const visibleExtractDates = useMemo(
    () => sliceByIndexRange(extractDates, chartRange),
    [chartRange, extractDates],
  );

  const sortedExtractRows = useMemo(
    () =>
      [...(data?.by_extract_date ?? [])].sort((a, b) =>
        a.extract_date.localeCompare(b.extract_date),
      ),
    [data?.by_extract_date],
  );

  const visibleExtractRows = useMemo(
    () => sliceByIndexRange(sortedExtractRows, chartRange),
    [chartRange, sortedExtractRows],
  );

  const visibleStateRows = useMemo(() => {
    const dates = new Set(visibleExtractDates);
    return (data?.by_state_over_time ?? []).filter((row) =>
      dates.has(row.extract_date),
    );
  }, [data?.by_state_over_time, visibleExtractDates]);

  const handleChartRangeSelect = useCallback((viewRange: ChartIndexRange) => {
    setChartRange((current) => mergeViewRangeIntoAbsolute(viewRange, current));
  }, []);

  const chartRangeLabel = useMemo(() => {
    if (!chartRange || visibleExtractDates.length === 0) {
      return undefined;
    }
    const start = visibleExtractDates[0];
    const end = visibleExtractDates[visibleExtractDates.length - 1];
    return start === end ? start : `${start} – ${end}`;
  }, [chartRange, visibleExtractDates]);

  const latestSummary = useMemo(
    () => (data ? summarizeLatestExtract(data) : null),
    [data],
  );

  if (!isActive) {
    return null;
  }

  if (isLoading) {
    return <LoadingScreen message="Loading ATTOM tax assessor status..." />;
  }

  if (error) {
    return (
      <ErrorComponent
        title="Failed to load ATTOM status"
        message={getQueryErrorMessage(error)}
        action={{
          label: "Retry",
          onClick: () => void refetch(),
        }}
      />
    );
  }

  const headerAction = (
    <div className="flex flex-wrap items-end gap-3">
      <div className="min-w-[180px] space-y-2">
        <label htmlFor="attom-state-filter" className="text-sm font-medium">
          State
        </label>
        <Select
          options={stateOptions}
          value={stateFilter ? [stateFilter] : [""]}
          onValueChange={(values) =>
            setStateFilter(values[0] === "" ? "" : (values[0] ?? ""))
          }
          placeholder="All states"
          triggerClassName="w-[180px]"
        />
      </div>
      <Button
        type="button"
        variant="outline"
        onClick={() => void refetch()}
        disabled={isFetching}
      >
        {isFetching ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Refreshing
          </>
        ) : (
          <>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </>
        )}
      </Button>
    </div>
  );

  return (
    <div className="w-full min-w-[768px] space-y-8">
      {embedded ? (
        <div className="flex justify-end">{headerAction}</div>
      ) : (
        <DashboardHeader
          title={getPortalPageTitleForSection(section)}
          action={headerAction}
        />
      )}

      <section className="space-y-2">
        <h2 className="text-sm font-medium text-muted-foreground">
          Current status
        </h2>
        <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide scroll-smooth">
          <AttomStatCard
            title="Latest extract"
            value={data?.latest_extract_date ?? "—"}
            description="Most recent ATTOM tax_assessor partition"
          />
          <AttomStatCard
            title="Extract dates scanned"
            value={formatCount(data?.extract_dates_scanned)}
            description={`Up to ${formatCount(data?.max_extract_dates)} configured`}
          />
          <AttomStatCard
            title="Records (latest)"
            value={formatCount(latestSummary?.latestRecords)}
            description={
              stateFilter
                ? `Filtered to ${stateFilter}`
                : "Across the latest extract date"
            }
          />
          <AttomStatCard
            title="States in latest"
            value={formatCount(data?.latest_by_state.length)}
            description="States represented in the newest extract"
          />
          <AttomStatCard
            title="Foreclosure cases"
            value={formatCount(
              integrationStatus?.jurisdiction_foreclosure_cases_count,
            )}
            description="Nationwide case rows indexed by jurisdiction and county FIPS"
          />
        </div>
      </section>

      {blurb ? <p className="text-sm text-muted-foreground">{blurb}</p> : null}

      {isFetching ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Refreshing Athena results…
        </p>
      ) : null}

      <section className="space-y-4">
        <ChartRangeResetButton
          range={chartRange}
          onReset={() => setChartRange(null)}
          rangeLabel={
            chartRangeLabel
              ? `extract dates ${chartRangeLabel}. Drag on a chart to adjust.`
              : undefined
          }
        />
        <AttomExtractHistoryChart
          rows={visibleExtractRows}
          onRangeSelect={handleChartRangeSelect}
        />
        <AttomRecordsByStateChart
          rows={visibleStateRows}
          extractDates={visibleExtractDates}
          onRangeSelect={handleChartRangeSelect}
        />
        <AttomJurisdictionsByStateChart
          rows={visibleStateRows}
          extractDates={visibleExtractDates}
          onRangeSelect={handleChartRangeSelect}
        />
      </section>
    </div>
  );
}
